/**
 * User Management › Advocates (ops mock version 13, §B4; Jeff, 28 October 2026) — every Advocate org's people, across
 * every zone. LIST AND DETACH ONLY.
 *
 * ⛔ STAFF NEVER ATTACH (ADR-113 D5/D15). Advocates join through the Advocate Onboarding Wizard, or convert from a
 * subscriber's account menu; an Advocate org declares its own legal entity and payout rail, which no staff member can
 * declare for it. Detach is the one staff action, and it is the access panel's own write
 * (`DELETE /admin/people/{sub}/access/advocate/{orgId}`) — this tab adds no new way to change an Advocate.
 *
 * The Payouts pill is the Advocate's half (payout details entered — the legal entity type declared at payout setup,
 * D16). Payouts themselves wait on Nexanomics' own Stripe activation, which the description line states once.
 */
import { useMemo, useState } from 'react';
import { FilterPills } from '../../../components/FilterPills';
import { FilterSearch } from '../../../components/FilterSearch';
import { ColumnTooltip } from '../../../components/ColumnTooltip';
import { ZoneBoardLegend } from '../../../components/ZoneBoardLegend';
import { ZoneCell, ZoneFanoutWarning } from '../../../components/ZoneIndicators';
import { useZoneReadReport } from '../../../components/ZoneContext';
import { useToast } from '../../../components/ToastProvider';
import { useReAuth, ReauthCancelledError } from '../../../lib/reauth';
import { actionErrorToast } from '../../../lib/friendly-error';
import { ZONES, consoleZoneLabel, zoneByCode, type PortalZone } from '../../../lib/zones';
import { ALL_ZONES, countInZone, matchesZoneFilter, zoneBoardLegend } from '../../../lib/zone-board';
import { listAdvocates, removeAccessEntry, type AdvocateOrgRow } from '../../../lib/person-access-api';
import { ActionConfirmModal } from '../ActionConfirmModal';
import { ActionIcon, LoadFailed, Loading, PersonEye, fmtDate, useZoneList, type OpenPerson } from './shared';

/** One person in one Advocate org — the row this tab shows. */
export interface AdvocateRow {
  cognitoSub: string;
  email: string;
  displayName: string | null;
  addedAt: string | null;
  org: Omit<AdvocateOrgRow, 'members'>;
}

const ORIGIN_LABEL: Record<AdvocateOrgRow['origin'], string> = { public: 'Public AOW', subscriber: 'Account menu' };
type StatusKey = 'active' | 'suspended';

/** Flatten an org into its people. An org with nobody in it is not a person to list. */
export const advocateRows = (orgs: AdvocateOrgRow[]): AdvocateRow[] =>
  orgs.flatMap(({ members, ...org }) => members.map((m) => ({ ...m, org })));

const readAdvocates = async (z: PortalZone): Promise<AdvocateRow[]> => advocateRows(await listAdvocates(z));

export function AdvocatesTab({ onOpenPerson }: { onOpenPerson: OpenPerson }) {
  const toast = useToast();
  const reauth = useReAuth();
  const { rows, results, failed, failedZones, reload } = useZoneList<AdvocateRow>(readAdvocates, 'the advocates');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<StatusKey>>(new Set<StatusKey>(['active', 'suspended']));
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);
  const [payouts, setPayouts] = useState<'any' | 'entered' | 'not_yet'>('any');
  const [confirm, setConfirm] = useState<(AdvocateRow & { zone: string }) | null>(null);
  const [busy, setBusy] = useState(false);

  const all = useMemo(() => rows ?? [], [rows]);
  useZoneReadReport({ rows, failedZones, filter: zoneFilter });
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((a) => selected.has(a.org.status) && matchesZoneFilter(a, zoneFilter)
      && (payouts === 'any' || (payouts === 'entered') === a.org.payoutDetails)
      && (!q || `${a.displayName ?? ''} ${a.email} ${a.org.name}`.toLowerCase().includes(q)));
  }, [all, selected, zoneFilter, payouts, search]);
  const count = (k: StatusKey) => all.filter((a) => a.org.status === k).length;
  const withPayouts = all.filter((a) => a.org.payoutDetails).length;
  const nameOf = (a: AdvocateRow) => a.displayName || a.email || a.cognitoSub;

  const detach = async () => {
    if (!confirm) return;
    const zone = zoneByCode(confirm.zone);
    setBusy(true);
    try {
      await reauth.withReauth(() => removeAccessEntry(confirm.cognitoSub, { kind: 'advocate', id: confirm.org.orgId }, zone));
      toast.push({
        variant: 'success',
        title: 'Advocate detached',
        detail: `${nameOf(confirm)} is no longer a member of ${confirm.org.name || 'the Advocate org'}. Their own sign-in and any account they belong to are untouched.`,
      });
      setConfirm(null);
      void reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      toast.push({ variant: 'error', ...actionErrorToast(err, { action: 'Detach advocate', subject: nameOf(confirm), zoneLabel: consoleZoneLabel(confirm.zone) }) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="users-tab-panel" aria-label="Advocates" data-testid="users-tab-advocates">
      <h2 className="users-tab-heading">
        Advocates
        {rows && <span className="portal-badge">{all.length} · {count('active')} active · {count('suspended')} suspended · {withPayouts} with payout details</span>}
      </h2>
      <p className="panel-help">
        Advocates join through the Advocate Onboarding Wizard, or convert from a subscriber&rsquo;s account menu; there is
        no staff invite — <b>Detach</b> is the only staff action here. The <b>Payouts</b> pill says whether the Advocate has
        entered their payout details; payouts themselves open when Nexanomics&rsquo; own Stripe activation is done.
      </p>

      <div className="portal-applications-controls">
        <FilterSearch value={search} onChange={setSearch} placeholder="Search name, business or email…" ariaLabel="Search advocates" />
        <FilterPills
          ariaLabel="Filter advocates by status"
          selected={selected}
          onChange={setSelected}
          groups={[
            { key: 'active', label: 'Active', icon: '✅', count: count('active') },
            { key: 'suspended', label: 'Suspended', icon: '⏸️', count: count('suspended') },
          ]}
        />
      </div>
      <div className="portal-users-filters">
        <label className="portal-users-filter-label">
          Zone
          <select className="portal-select" aria-label="Zone filter" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            <option value={ALL_ZONES}>All zones</option>
            {ZONES.map((z) => <option key={z.code} value={z.code}>{z.label}</option>)}
          </select>
        </label>
        <label className="portal-users-filter-label">
          Payouts
          <select className="portal-select" value={payouts} onChange={(e) => setPayouts(e.target.value as 'any' | 'entered' | 'not_yet')}>
            <option value="any">Any</option>
            <option value="entered">Payout details entered</option>
            <option value="not_yet">Payout details not yet</option>
          </select>
        </label>
      </div>
      <ZoneFanoutWarning failedZones={failedZones} noun="advocates" onRetry={() => void reload()} />
      <ZoneBoardLegend
        testId="users-advocates-legend"
        ariaLabel="Advocates by zone"
        loading={rows === null}
        columns={[{ key: 'all', label: 'Advocates' }, { key: 'active', label: 'Active' }, { key: 'payouts', label: 'Payout details' }]}
        rows={zoneBoardLegend(results, {
          counts: (code) => ({
            all: countInZone(all, code),
            active: countInZone(all, code, (a) => a.org.status === 'active'),
            payouts: countInZone(all, code, (a) => a.org.payoutDetails),
          }),
        })}
      />
      <p className="legend-cap">Counted over every row, whatever the filter shows.</p>

      {rows === null && <Loading text="Loading the advocates…" />}
      {failed && <LoadFailed what="the advocates" onRetry={() => void reload()} />}
      {rows !== null && !failed && (
        <table className="portal-users-table" data-testid="users-advocates-table">
          <thead>
            <tr>
              <th scope="col">Advocate</th>
              <th scope="col">Business · email</th>
              <th scope="col">Zone</th>
              <th scope="col">
                <span className="portal-col-help-wrap">Origin
                  <ColumnTooltip columnName="Origin" items={[
                    { term: 'Public AOW', desc: 'Joined through the public Advocate Onboarding Wizard — paid per qualifying referral.' },
                    { term: 'Account menu', desc: "Converted from a subscriber's account menu — paid from the fifth qualifying referral." },
                  ]} />
                </span>
              </th>
              <th scope="col">
                <span className="portal-col-help-wrap">Payouts
                  <ColumnTooltip columnName="Payouts" items={[
                    { term: 'Payout details entered', desc: 'The Advocate has declared its legal entity at payout setup.' },
                    { term: 'Payout details not yet', desc: 'Nothing entered yet; referrals still count.' },
                  ]} />
                </span>
              </th>
              <th scope="col">Status</th>
              <th scope="col">Joined</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={8} className="portal-users-empty">
                {selected.size === 0 ? 'No filter pills selected — click a status pill to show advocates.' : all.length === 0 ? 'No advocates yet.' : 'No advocates match the current filters.'}
              </td></tr>
            )}
            {shown.map((a) => (
              <tr key={`${a.zone}:${a.org.orgId}:${a.cognitoSub}`} data-status={a.org.status}>
                <td><div className="portal-users-name">{nameOf(a)}</div></td>
                <td>
                  <div>{a.org.name || '—'}</div>
                  <div className="portal-users-sub-email">{a.email || '—'}</div>
                </td>
                <td><ZoneCell zone={a.zone} /></td>
                <td>{ORIGIN_LABEL[a.org.origin]}</td>
                <td>
                  {a.org.payoutDetails
                    ? <span className="portal-badge portal-badge-ok">Payout details entered</span>
                    : <span className="portal-badge portal-badge-pending">Payout details not yet</span>}
                </td>
                <td>
                  {a.org.status === 'active'
                    ? <span className="portal-badge portal-badge-active">Active</span>
                    : <span className="portal-badge portal-badge-paused">Suspended</span>}
                </td>
                <td className="portal-users-created">{fmtDate(a.addedAt)}</td>
                <td className="portal-users-row-actions portal-actions-col">
                  <div className="portal-actions-row">
                    <PersonEye name={nameOf(a)} onOpen={() => onOpenPerson(a.cognitoSub, a.zone)} />
                    <ActionIcon act="detach" icon="detach" label={`Detach ${nameOf(a)} from the Advocate programme`}
                      tip="Detach — the only staff action; advocates attach themselves through the AOW"
                      onClick={() => setConfirm(a)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ActionConfirmModal
        open={confirm !== null}
        title="Detach this advocate?"
        intent="danger"
        primaryLabel="Detach"
        loading={busy}
        onConfirm={() => void detach()}
        onCancel={() => setConfirm(null)}
      >
        {confirm && (
          <p>
            <b>{nameOf(confirm)}</b> is detached from <b>{confirm.org.name || 'the Advocate org'}</b>: no new referrals are
            attributed to them through it. Their own sign-in and any subscriber account they belong to are untouched.
            Re-joining is theirs to do, through the Advocate Onboarding Wizard — staff cannot attach them back.
          </p>
        )}
      </ActionConfirmModal>
    </section>
  );
}
